import { differenceInDays, parseISO } from 'date-fns';

export const DECLINED_REASONS = [
  { value: 'had_before', label: 'Had before' },
  { value: 'transfer_familiar', label: 'Transfer - patient familiar' },
  { value: 'patient_declined', label: 'Patient declined' },
  { value: 'caregiver_declined', label: 'Caregiver/agent declined' },
  { value: 'left_voicemail', label: 'Left voicemail' },
  { value: 'other', label: 'Other' },
] as const;

export type DeclinedReasonValue = (typeof DECLINED_REASONS)[number]['value'];

const DECLINED_REASON_LABELS: Record<string, string> = DECLINED_REASONS.reduce(
  (acc, r) => {
    acc[r.value] = r.label;
    return acc;
  },
  {} as Record<string, string>,
);

/**
 * Map a counseling declined-reason code (e.g. `transfer_familiar`) to its
 * human-readable label. Unknown/legacy codes are prettified from the raw slug
 * (underscores → spaces, title-cased) so we never leak a machine enum to the UI
 * — GP-1110 (raw `transfer_familiar` shown on RX History / Hardcopy).
 */
export function getDeclinedReasonLabel(reason: string | undefined | null): string {
  if (!reason) return '';
  const known = DECLINED_REASON_LABELS[reason];
  if (known) return known;
  // Composite/free-text reasons ("Other: <text>", "Left voicemail at <ts>",
  // "Order cancelled") are human-authored — return them untouched. Only bare
  // machine slugs (no whitespace) get prettified.
  if (/\s/.test(reason)) return reason;
  return reason
    .replace(/_/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * GP-1485 — what a counsellor whose identity can't be resolved is called on
 * screen. Deliberately neutral: a raw Auth0 `sub` (or a truncated fragment of
 * one) is not an answer to "who counselled this patient", it's a leak.
 */
export const UNKNOWN_COUNSELOR_LABEL = 'Unknown user';

/**
 * True when a value is a user IDENTIFIER rather than a human name — an Auth0
 * `sub` (`auth0|6a7a…`, `google-oauth2|…`), the `@auth0.local` fallback email a
 * JWT carries when the tenant has no email claim, or the `User 6a7a04`
 * placeholder `/api/auth/user-lookup` returns for an id it could not find
 * (backend `auth.controller.ts`). None of these belong on screen.
 */
function isUserIdentifier(value: string): boolean {
  return (
    value.includes('|') ||
    value.toLowerCase().endsWith('@auth0.local') ||
    /^user\s+[0-9a-f]{6,}$/i.test(value)
  );
}

/**
 * GP-1485 — resolve a counseling record's stored actor (`completedBy` /
 * `createdBy`) to a display name.
 *
 * The record stores whatever the JWT carried at sign-off: usually an email, but
 * an Auth0 `sub` (`auth0|6a7a0473e5631ce99ac29646@auth0.local`) when there is no
 * email claim. The order-details Counseling History table interpolated that
 * value straight into `By: {value}` and printed the raw sub in the Details
 * column, two cards away from a timeline that resolves the same kind of id fine.
 *
 * `lookup` is the resolved-names map from `useBatchUserLookup` — the same
 * `/api/auth/user-lookup` path the Order Timeline and the Verification block
 * use. An identifier that can't be resolved yields
 * {@link UNKNOWN_COUNSELOR_LABEL}, never the identifier and never a fragment of
 * it. An empty value means no counsellor is recorded yet (a pending session) and
 * reads as an em dash rather than claiming the user is unknown.
 */
export function getCounselorDisplayName(
  value: string | null | undefined,
  lookup?: (id: string) => string | null | undefined,
): string {
  const raw = (value || '').trim();
  if (!raw) return '—';

  const resolved = (lookup?.(raw) || '').trim();
  if (resolved && !isUserIdentifier(resolved)) return resolved;

  // Already human-readable (a name, or the email most records carry).
  if (!isUserIdentifier(raw)) return raw;

  return UNKNOWN_COUNSELOR_LABEL;
}

/**
 * Derive the hardcopy counsel-checkbox flags (DID Counsel / In Person / Via Phone /
 * Agent / NOT Counsel / Pat Refused / …) from a counseling record so the method and
 * decline status captured during counseling carry over to the hardcopy — GP-1110
 * ("by phone" counseling was not checking the Via Phone box). Any explicit flags
 * already on the record (`existing`, from clinical verification) are used as the
 * base and only overridden where the counseling record has an opinion.
 *
 * GP-1358: only a FINISHED session ticks "DID Counsel". A record sitting at
 * `pending` (the status a newly created counseling task carries) or `accepted`
 * means counseling is still owed — see {@link getCounselingIndicatorState} — and
 * `cancelled` / `superseded` mean it never happened here. Previously any truthy
 * status ticked the box, so a hardcopy printed from a chart with an OPEN
 * counseling task asserted, on paper, that the patient had been counselled. The
 * method / agent boxes are left status-independent: they describe HOW the session
 * was (or is being) conducted, and a declined-by-phone record must still show
 * Via Phone.
 */
export function deriveCounselFlagsFromCounseling(
  record:
    | {
        method?: string | null;
        counselingBy?: string | null;
        status?: string | null;
        declinedReason?: string | null;
      }
    | null
    | undefined,
  existing?: Record<string, boolean> | null,
): Record<string, boolean> {
  const flags: Record<string, boolean> = { ...(existing || {}) };
  if (!record) return flags;

  const method = (record.method || '').toLowerCase();
  if (method === 'by_phone' || method === 'phone') flags.viaPhone = true;
  if (method === 'in_person' || method === 'in-person') flags.inPerson = true;

  const by = (record.counselingBy || '').toLowerCase();
  if (by === 'caregiver' || by === 'representative') flags.agent = true;

  const { isDeclined } = getCounselingStatusDisplay(record.status, record.declinedReason);
  if (isDeclined) {
    flags.notCounsel = true;
    flags.didCounsel = false;
    const reason = (record.declinedReason || '').toLowerCase();
    if (reason === 'patient_declined' || reason === 'caregiver_declined') flags.patRefused = true;
  } else if ((record.status || '').toLowerCase() === 'completed') {
    flags.didCounsel = true;
    flags.notCounsel = false;
  }

  return flags;
}

/**
 * Pick the counseling record that should drive a given Rx's hardcopy counsel
 * checkboxes from the flat `/api/counseling/by-order` list — GP-1110 (the
 * order-details hardcopy showed all boxes unchecked because it never looked at
 * the order's counseling records). The latest record for the Rx wins. Records
 * carrying a DIFFERENT rxNumber are never borrowed; records with no rxNumber
 * are used only when `fallbackToUnlabeled` is set (single-hardcopy orders,
 * where every record of the order belongs to its one prescription).
 */
export function pickCounselingRecordForRx<
  T extends { rxNumber?: string | number | null; createdAt?: string | null },
>(
  records: T[] | null | undefined,
  rxNumber: string | number | null | undefined,
  opts: { fallbackToUnlabeled?: boolean } = {},
): T | undefined {
  if (!records?.length) return undefined;

  const hasRx = rxNumber !== null && rxNumber !== undefined && String(rxNumber) !== '';
  const matches = hasRx
    ? records.filter((r) => r.rxNumber != null && String(r.rxNumber) === String(rxNumber))
    : [];
  const pool = matches.length
    ? matches
    : opts.fallbackToUnlabeled
      ? records.filter((r) => r.rxNumber == null || String(r.rxNumber) === '')
      : [];
  if (!pool.length) return undefined;

  // The API returns createdAt DESC, but sort defensively so callers never
  // depend on response ordering.
  return [...pool].sort(
    (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime(),
  )[0];
}

/**
 * GP-1137 — a COPIED prescription (Rx.CopiedFrom set) is only a *continuation*
 * of existing therapy — which defaults counselling to Declined — when the drug
 * was ACTUALLY dispensed before AND that last real fill is no older than this
 * many days. ~6 months, matching the refill-reassessment window.
 */
export const COUNSELING_CONTINUATION_MAX_GAP_DAYS = 180;

/**
 * Whole days between a date-only string (YYYY-MM-DD or ISO) and `today`, parsed
 * as LOCAL midnight so an evening timestamp never rolls to the wrong day (see the
 * project Date Handling Rules). Returns null for empty/unparseable input.
 */
function daysSinceDateOnly(
  dateStr: string | null | undefined,
  today: Date,
): number | null {
  if (!dateStr) return null;
  const datePart = String(dateStr)
    .replace(/Z$/, '')
    .replace(/[+-]\d{2}:\d{2}$/, '')
    .split('T')[0];
  if (!datePart) return null;
  const parsed = parseISO(`${datePart}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return differenceInDays(today, parsed);
}

/**
 * GP-1137 — decide whether a copied Rx should be treated as a continuation of
 * existing therapy (counselling declined by default) rather than a fresh new
 * start (counselling required).
 *
 * The old rule keyed purely off `Rx.CopiedFrom` being set, so a copy of a
 * prescription that was NEVER actually dispensed (the prior Rx sat Unfilled) was
 * mislabelled "counselling not required" — yet a never-dispensed first fill of a
 * (new) drug is exactly the case that MUST be counselled. A copy is a genuine
 * continuation only when BOTH hold:
 *   - the lineage was actually dispensed before (a real last-fill date exists), AND
 *   - that last real fill is within {@link COUNSELING_CONTINUATION_MAX_GAP_DAYS}.
 * Otherwise (never dispensed, or the gap exceeds the window) it is a new start
 * and counselling is required.
 *
 * @param isCopiedFill     Rx.CopiedFrom is set (hardcopy `copiedRxNum`).
 * @param lastRealFillDate The lineage's last ACTUALLY-dispensed fill date
 *                         (hardcopy `lastFillDate`); null/absent when never dispensed.
 * @param today            Injectable "now" for deterministic tests.
 */
export function isCopiedFillContinuation(
  isCopiedFill: boolean,
  lastRealFillDate: string | null | undefined,
  today: Date = new Date(),
): boolean {
  if (!isCopiedFill) return false;
  const days = daysSinceDateOnly(lastRealFillDate, today);
  if (days == null) return false; // never actually dispensed → new start
  return days <= COUNSELING_CONTINUATION_MAX_GAP_DAYS;
}

/**
 * GP-1155 — counseling `reason` codes that are NOT patient counseling sessions.
 *
 * Clinical Verification writes a counseling row for two acknowledgment events —
 * acknowledging a safety/allergy warning ("No allergy information in Kroll…") and
 * overriding a drug-interaction check. Those rows carry no Rx/drug and an empty
 * 0/13 checklist, yet default `status: 'completed'`, so they render in the patient
 * "Counseling History" table as a blank "Completed" session that misrepresents an
 * acknowledgment as a finished counseling. The same acknowledgment is already
 * captured verbatim in the auto-created clinical note (patient file → All Notes),
 * so the counseling-sessions table must exclude these reasons.
 */
export const ACKNOWLEDGMENT_COUNSELING_REASONS = [
  'safety_acknowledgment',
  'clinical_interaction_override',
] as const;

/**
 * True when a counseling record is a clinical-verification acknowledgment
 * (see {@link ACKNOWLEDGMENT_COUNSELING_REASONS}) rather than a real patient
 * counseling session. Used to keep these rows out of the patient Counseling
 * History table (GP-1155).
 */
export function isAcknowledgmentCounselingRecord(
  record: { reason?: string | null } | null | undefined,
): boolean {
  const reason = (record?.reason || '').toLowerCase();
  return (ACKNOWLEDGMENT_COUNSELING_REASONS as readonly string[]).includes(reason);
}

export type CounselingStatusColor = 'success' | 'error' | 'warning' | 'default';

export interface CounselingStatusDisplay {
  label: string;
  color: CounselingStatusColor;
  /** True when the record represents a declined counseling (incl. the legacy
   *  completed+declinedReason shape written by the old decline path). */
  isDeclined: boolean;
  /** GP-1203: the counseling was satisfied by a later one on a rebuilt Rx —
   *  nothing is left to action, so no Overdue chip and no Start Counseling. */
  isSuperseded: boolean;
}

/**
 * GP-1203: status value the backend stamps on a counseling record/task whose Rx
 * was cancelled and rebuilt, and whose counseling completed on the replacement
 * Rx. Mirrors SUPERSEDED_STATUS in backend-v2 counseling-supersession.util.ts.
 */
export const SUPERSEDED_COUNSELING_STATUS = 'superseded';

/**
 * Single source of truth for how a counseling record's status renders.
 *
 * A counseling that was declined is persisted in two shapes across the codebase:
 *   - status === 'declined'                                  (correct path)
 *   - status === 'completed' with a declinedReason set       (legacy decline-via-dialog)
 * Both MUST display as "Declined". Treating raw status only is the B2 bug.
 */
export function getCounselingStatusDisplay(
  status: string | undefined | null,
  declinedReason?: string | null,
): CounselingStatusDisplay {
  const s = (status || '').toLowerCase();
  const declined = s === 'declined' || (s === 'completed' && !!declinedReason);

  if (declined) return { label: 'Declined', color: 'error', isDeclined: true, isSuperseded: false };
  if (s === 'completed') return { label: 'Completed', color: 'success', isDeclined: false, isSuperseded: false };
  if (s === 'accepted') return { label: 'Accepted', color: 'success', isDeclined: false, isSuperseded: false };
  if (s === 'pending') return { label: 'Pending', color: 'warning', isDeclined: false, isSuperseded: false };
  if (s === 'cancelled') return { label: 'Cancelled', color: 'default', isDeclined: false, isSuperseded: false };
  if (s === SUPERSEDED_COUNSELING_STATUS) {
    return { label: 'Superseded', color: 'default', isDeclined: false, isSuperseded: true };
  }

  return {
    label: s ? s.charAt(0).toUpperCase() + s.slice(1) : '—',
    color: 'default',
    isDeclined: false,
    isSuperseded: false,
  };
}

/**
 * GP-1040 — the THREE states of the per-Rx counseling indicator (the chip on
 * Clinical Verification / Packaging):
 *   - `required`  — counseling is still owed; the task sits in the queue
 *   - `declined`  — counseling was declined (with a reason)
 *   - `completed` — counseling actually happened; the queue task is cleared
 *
 * Before this the chip was a two-state toggle, so a counseling that had been
 * COMPLETED (in clinical, or from the task queue) kept rendering as
 * "Counseling Required" — completed counsels looked pending.
 */
export type CounselingIndicatorState = 'required' | 'declined' | 'completed';

/**
 * GP-1040 — resolve the three-state counseling indicator for an Rx from its
 * latest counseling record.
 *
 * `defaultRequired` is the no-record default the caller derives from the fill
 * itself: a brand-new Rx defaults to REQUIRED, a bracket-copied first fill that
 * is a genuine continuation defaults to DECLINED (see
 * {@link isCopiedFillContinuation}) — and stays toggleable either way.
 *
 * Records that are not real counseling sessions never report a drug as
 * counseled: clinical-verification acknowledgments (GP-1155) and records
 * cancelled with their order fall back to the caller's default. Declined is
 * resolved through {@link getCounselingStatusDisplay}, so the legacy
 * completed+declinedReason shape reads as declined, not completed.
 */
export function getCounselingIndicatorState(
  record:
    | {
        status?: string | null;
        declinedReason?: string | null;
        reason?: string | null;
      }
    | null
    | undefined,
  defaultRequired: boolean,
): CounselingIndicatorState {
  const fallback: CounselingIndicatorState = defaultRequired ? 'required' : 'declined';
  const status = (record?.status || '').toLowerCase();
  if (!record || !status) return fallback;
  if (isAcknowledgmentCounselingRecord(record)) return fallback;
  if (status === 'cancelled') return fallback;

  const { isDeclined } = getCounselingStatusDisplay(record.status, record.declinedReason);
  if (isDeclined) return 'declined';
  if (status === 'completed') return 'completed';
  // pending / accepted — the session is open, counseling is still owed.
  return 'required';
}
